import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFieldStore } from '@/store/field.store';
import { useMapUIStore } from '@/store/map.store';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { CreateIcon, DamageIcon, LayersIcon, ListIcon, PlusIcon, RoadIcon, TaskIcon, UsersIcon } from '../icons';

interface TaskStats {
  open: number;
  assigned: number;
  in_progress: number;
  pending_review: number;
  complete_today: number;
  my_tasks: number;
  overdue: number;
}

interface ActionButtonProps {
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  badge?: number;
}

function ActionButton({ onClick, icon: Icon, label, active, badge }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center flex-1 py-2 px-1 min-w-0 touch-manipulation transition-colors ${
        active
          ? 'text-primary-600 dark:text-primary-400'
          : 'text-theme-secondary active:text-gray-900 dark:active:text-white dark:active:text-gray-200'
      }`}
    >
      <div className="relative">
        <Icon className="h-6 w-6" />
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 h-4 min-w-[1rem] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className="text-xs mt-1 truncate max-w-full">{label}</span>
    </button>
  );
}

export default function FieldBottomNav() {
  const { drawerState, drawerTab, setDrawerState, setDrawerTab } = useFieldStore();
  const { toggleLayerPanel } = useMapUIStore();
  const { activeTenant } = useTenantStore();
  const { activeIncident } = useIncidentStore();

  // Task stats via React Query — auto-updates on socket invalidation + 30s polling fallback
  const taskStatsParams = activeIncident?.id ? { incident_id: activeIncident.id } : {};
  const { data: taskStats } = useTenantQuery<TaskStats>(
    ['tasks', 'stats', activeIncident?.id ?? 'all'],
    '/tasks/stats',
    {
      params: taskStatsParams,
      enabled: !!activeTenant,
      queryOptions: { refetchInterval: 30000 },
    }
  );
  const taskCount = taskStats?.my_tasks ?? 0;

  // Roster count via React Query — auto-updates on socket invalidation + 30s polling fallback
  const { data: rosterData } = useTenantQuery<{ data: Array<{ roster_count?: number }> }>(
    ['ics214', 'roster-count', activeIncident?.id],
    '/ics214',
    {
      params: { incident_id: activeIncident?.id, status: 'active', limit: 1 },
      enabled: !!activeTenant && !!activeIncident?.id,
      queryOptions: { refetchInterval: 30000 },
    }
  );
  const rosterLogs = rosterData?.data || (rosterData as unknown as Array<{ roster_count?: number }>);
  const rosterCount = (Array.isArray(rosterLogs) ? rosterLogs[0]?.roster_count : undefined) ?? 0;

  const handleTasksClick = () => {
    if (drawerState !== 'closed' && drawerTab === 'tasks') {
      setDrawerState('closed');
    } else {
      setDrawerTab('tasks');
      setDrawerState('half');
    }
  };

  const handleListClick = () => {
    if (drawerState !== 'closed' && drawerTab !== 'tasks' && drawerTab !== 'team') {
      setDrawerState('closed');
    } else {
      if (drawerTab === 'tasks' || drawerTab === 'team') {
        setDrawerTab('damage');
      }
      setDrawerState('half');
    }
  };

  const handleTeamClick = () => {
    if (drawerState !== 'closed' && drawerTab === 'team') {
      setDrawerState('closed');
    } else {
      setDrawerTab('team');
      setDrawerState('half');
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { setDrawingMode } = useFieldStore();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  // Close create menu on outside click
  useEffect(() => {
    if (!showCreateMenu) return;
    const handler = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCreateMenu]);

  const handleCreateClick = () => {
    setShowCreateMenu((prev) => !prev);
  };

  const isMapRoute = location.pathname === '/field';

  const createOptions = [
    { label: 'Damage Assessment', icon: DamageIcon, path: '/field/damage/new' },
    { label: 'Road Closure', icon: RoadIcon, path: null as string | null, action: () => {
      if (!isMapRoute) navigate('/field');
      setDrawingMode('road_closure');
    }},
    { label: 'Supply Request', icon: ListIcon, path: '/field/supplies/new' },
    { label: 'Task', icon: TaskIcon, path: null as string | null, action: () => {
      if (!isMapRoute) navigate('/field');
      setDrawingMode('task');
    }},
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-surface-elevated border-t border-gray-200 dark:border-gray-800 shadow-lg"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Create menu popover */}
      {showCreateMenu && (
        <div
          ref={createMenuRef}
          className="absolute bottom-full right-2 mb-2 w-52 bg-surface-elevated rounded-lg shadow-xl border border-theme-primary overflow-hidden z-50"
        >
          {createOptions.map((opt) => (
            <button
              key={opt.label}
              onClick={() => {
                if (opt.action) {
                  opt.action();
                } else if (opt.path) {
                  navigate(opt.path);
                }
                setShowCreateMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-theme-primary hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 touch-manipulation"
            >
              <opt.icon className="h-5 w-5 text-theme-tertiary" />
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-stretch h-16 max-w-lg mx-auto">
        <ActionButton
          onClick={handleTasksClick}
          icon={TaskIcon}
          label="Tasks"
          active={drawerState !== 'closed' && drawerTab === 'tasks'}
          badge={taskCount}
        />
        <ActionButton
          onClick={handleListClick}
          icon={ListIcon}
          label="List"
          active={drawerState !== 'closed' && drawerTab !== 'tasks' && drawerTab !== 'team'}
        />
        <ActionButton
          onClick={handleTeamClick}
          icon={UsersIcon}
          label="Team"
          active={drawerState !== 'closed' && drawerTab === 'team'}
          badge={rosterCount}
        />
        <ActionButton
          onClick={toggleLayerPanel}
          icon={LayersIcon}
          label="Layers"
        />
        <ActionButton
          onClick={handleCreateClick}
          icon={CreateIcon}
          label="Create"
          active={showCreateMenu}
        />
      </div>
    </nav>
  );
}
